import { AddressItem } from './mockState';

// Helper to check if query is a CEP (8 digits)
export const isCEP = (q: string): boolean => /^\d{5}-?\d{3}$/.test(q.replace(/\s/g, ''));

// Reverse geocoding: convert lat/lng to address
export async function reverseGeocode(lat: number, lng: number): Promise<AddressItem | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await res.json();

    if (!data.display_name) return null;

    const address: AddressItem = {
      label: data.display_name,
      city: data.address.city || data.address.town || data.address.village || data.address.county || '',
      uf: data.address.state || '',
      lat,
      lng
    };

    return address;
  } catch (e) {
    console.warn('Reverse geocoding failed', e);
    return null;
  }
}

// Fetch via ViaCEP
async function fetchViaCEP(cep: string): Promise<AddressItem | null> {
  const cleanCep = cep.replace(/\D/g, '');
  const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
  const data = await res.json();
  if (data.erro) return null;

  // Nominatim fallback for lat/lng based on the address from ViaCEP
  const fullAddress = `${data.logradouro}, ${data.bairro}, ${data.localidade} - ${data.uf}`;
  const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullAddress)}&format=json&limit=1`);
  const geoData = await geoRes.json();

  if (!geoData.length) return null;

  return {
    label: `${data.logradouro}${data.complemento ? ', ' + data.complemento : ''}${data.bairro ? ' - ' + data.bairro : ''}`,
    city: data.localidade,
    uf: data.uf,
    lat: parseFloat(geoData[0].lat),
    lng: parseFloat(geoData[0].lon)
  };
}

// Fetch via Nominatim (text search)
async function fetchNominatim(query: string): Promise<AddressItem[]> {
  const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=br`);
  const data = await res.json();

  return data.map((item: any) => ({
    label: item.display_name.split(',').slice(0, 2).join(',').trim(),
    city: item.address.city || item.address.town || item.address.village || item.address.county || '',
    uf: item.address.state || '',
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon)
  }));
}

export async function searchAddress(query: string): Promise<AddressItem[]> {
  if (!query.trim()) return [];

  const results: AddressItem[] = [];

  // Check if it's a CEP
  if (isCEP(query)) {
    const cepResult = await fetchViaCEP(query);
    if (cepResult) results.push(cepResult);
  }

  // Nominatim search (always try for richer results)
  try {
    const nomResults = await fetchNominatim(query);
    results.push(...nomResults);
  } catch (e) {
    console.warn('Nominatim search failed', e);
  }

  return results;
}